import { Injectable } from '@angular/core';
import { TaskResponse, FeedsGetTaskFeedGQL } from 'app/shared/graphql';
import { Observable, defer, EMPTY } from 'rxjs';
import { AppService } from 'app/app.service';
import { map, repeat, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface UserTaskItem {
    id: string;
    title: string;
    description: string;
    state: string;
    active: boolean;
    task: TaskResponse;
    error?: string;
}


@Injectable()
export class UserTaskService {

    constructor(private app: AppService, private i18n: TranslateService) {

    }

    getTaskFeed(entityId: string): Observable<UserTaskItem[]> {
        if (!this.app.api.websocketConnected) {
            return defer(() => this.app.api.tasks.listTasks(entityId, 0, 100, '', `linked=${entityId}`)).pipe(
                map(tasks => {
                    // Show active tasks and recently completed tasks (within last 5 minutes)
                    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
                    return tasks.items.filter(task => {
                        if (['pending', 'executing', 'waiting', 'canceling'].includes(task.state)) {
                            return true;
                        }
                        // Parse date string as UTC: "2026/03/12 09:11:28" -> "2026-03-12T09:11:28Z"
                        const completedDate = task.date?.completed ? new Date(task.date.completed.replace(/\//g, '-').replace(' ', 'T') + 'Z').getTime() : 0;
                        return completedDate > fiveMinutesAgo;
                    });
                }),
                map(tasks => tasks.map(task => this.mapTaskResponse(task as TaskResponse))),
                repeat({ delay: 10 * 1000 }),
                catchError(err => {
                    console.error('Failed to poll tasks:', err);
                    return EMPTY;
                })
            );
        }
        return new FeedsGetTaskFeedGQL(this.app.api.gql).subscribe({ variables: { entity: entityId }}).pipe(map(result => {
            return result.data.getTaskFeed.map(x => this.mapTaskResponse(x as TaskResponse));
        }));
    }

    async cancelTask(id: string): Promise<void> {
        await this.app.api.tasks.cancelTask(id);
    }

    mapTaskResponse(task: TaskResponse): UserTaskItem {
        const result: UserTaskItem = {
            id: task.id,
            title: this.i18n.instant(`TASKS.TYPES.${task.type.toUpperCase()}.TITLE`),
            description: this.i18n.instant(`TASKS.TYPES.${task.type.toUpperCase()}.DESCRIPTION`, task.data),
            state: this.i18n.instant(`TASKS.STATE.${task.state.toUpperCase()}`),
            active: ['pending', 'executing', 'waiting', 'canceling', ].includes(task.state),
            task: task,
            error: task.results && task.results.error && task.results.error.message,
        };
        return result;
    }

}
